import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Col,
  Button,
  Input,
} from "reactstrap";
import Select from "react-select";
import TableContainer from "../../components/Common/TableContainer";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getAbsentDays, saveAbsentDay } from "../../components/Api/GmsAbsentDay";
import { savePayrollDetail } from "../../components/Api/GmsPayrollDetail";
import axios from "axios";
import BASE_URL from "../../config";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin,
  hasApprovePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";

const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    'Authorization': `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

const AbsentDaysPage = () => {
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [payrollInfo, setPayrollInfo] = useState(null);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAbsentDays');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsAbsentDays');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsAbsentDays');
  const hasApprovePermission = hasApprovePermissionWithAdmin(permissions, '/GmsAbsentDays');
  const isAdmin = isUserAdmin();

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadCurrentPayroll(),
        loadBusinesses(),
        loadDesignations(),
        loadLeaveTypes(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  const loadCurrentPayroll = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/Payroll/get`, {}, {
        headers: getAuthHeader(),
      });
      
      // Get the current active payroll (IsPosted = false)
      const currentPayroll = response.data.find(p => !p.payroll_IsPosted);
      if (!currentPayroll) {
        throw new Error('No active payroll found');
      }
      setPayrollInfo(currentPayroll);
    } catch (error) {
      console.error("Error loading payroll:", error);
      Swal.fire("Error", "Failed to load payroll information", "error");
    }
  };

  const loadBusinesses = async () => {
    try {
      const response = await getBusinesses({});
      if (response.isSuccess || Array.isArray(response)) {
        const data = response.data || response;
        const businessOptions = data.map(b => ({
          value: b.business_Id,
          label: b.business_NameEn
        }));
        setBusinesses(businessOptions);
      }
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load businesses", "error");
    }
  };

  const loadBranches = async (businessId) => {
    try {
      const response = await getBranches({ Business_Id: businessId });
      const branchData = Array.isArray(response) ? response : (response.data || []);
      const branchOptions = branchData.map(b => ({
        value: b.branch_Id,
        label: b.branch_NameEn || b.branch_Name
      }));
      setBranches(branchOptions);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadDepartments = async (branchId) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/Department/get`, {
        Branch_Id: branchId
      }, {
        headers: getAuthHeader(),
      });
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const departmentOptions = data.map(d => ({
        value: d.department_Id,
        label: d.department_NameEn || d.department_Name
      }));
      setDepartments(departmentOptions);
    } catch (error) {
      console.error("Error loading departments:", error);
      Swal.fire("Error", "Failed to load departments", "error");
    }
  };

  const loadDesignations = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/Designation/get`, {}, {
        headers: getAuthHeader(),
      });
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setDesignations(data);
    } catch (error) {
      console.error("Error loading designations:", error);
    }
  };

  const loadLeaveTypes = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/LeaveType/get`, {}, {
        headers: getAuthHeader(),
      });
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setLeaveTypes(data);
    } catch (error) {
      console.error("Error loading leave types:", error);
    }
  };

  // Function to calculate cumulative slab-based incentive for performance (type 1)
  const calculateSlabBasedIncentive = (totalAmount, grossSalary, slabs) => {
    console.log(`\n=== PERFORMANCE INCENTIVE CALCULATION ===`);
    console.log(`Total Performance: ${totalAmount}, Gross Salary: ${grossSalary}`);
    
    if (totalAmount <= grossSalary || slabs.length === 0) {
      console.log(`No incentive: totalAmount (${totalAmount}) <= grossSalary (${grossSalary}) or no slabs`);
      return 0;
    }
    
    const eligibleAmount = totalAmount - grossSalary; // Only amount above gross salary is eligible
    console.log(`Eligible Amount: ${totalAmount} - ${grossSalary} = ${eligibleAmount}`);
    
    let totalIncentive = 0;
    let currentThreshold = grossSalary;
    
    // Sort slabs by fromSalaryMultiple to ensure proper order
    const sortedSlabs = [...slabs].sort((a, b) => a.fromSalaryMultiple - b.fromSalaryMultiple);
    
    for (const slab of sortedSlabs) {
      const fromAmount = slab.fromSalaryMultiple * grossSalary;
      const toAmount = slab.toSalaryMultiple ? slab.toSalaryMultiple * grossSalary : Infinity;
      
      console.log(`Checking Slab: ${fromAmount} to ${toAmount} (${slab.percentage}%)`);
      
      if (totalAmount > fromAmount) {
        // Calculate the amount that falls within this slab
        const slabStart = Math.max(currentThreshold, fromAmount);
        const slabEnd = Math.min(totalAmount, toAmount);
        const slabAmount = Math.max(0, slabEnd - slabStart);
        
        if (slabAmount > 0) {
          const incentive = (slabAmount * (slab.percentage || 0)) / 100;
          totalIncentive += incentive;
          
          console.log(`Slab ${slab.number}: ${slabAmount} × ${slab.percentage}% = ${incentive}`);
          console.log(`Running Total: ${totalIncentive}`);
        }
      }
      
      currentThreshold = fromAmount;
    }
    
    console.log(`Final Cumulative Incentive: ${totalIncentive}`);
    return totalIncentive;
  };

  // Function to calculate flat rate incentive for sales (type 2)
  const calculateFlatRateIncentive = (totalAmount, rates) => {
    if (totalAmount <= 0 || rates.length === 0) {
      return 0;
    }
    
    const rate = rates[0]; // Take the first (and likely only) rate for type 2
    const incentive = (totalAmount * (rate.percentage || 0)) / 100;
    return incentive;
  };

  const loadEmployees = async () => {
    if (!selectedBusiness || !payrollInfo) {
      Swal.fire("Error", "Please wait for payroll information to load", "error");
      return;
    }
    
    setLoading(true);
    try {
      const filter = {
        employee_Business_Id: selectedBusiness.value,
        OnWork: true
      };
      
      // Only add branch filter if branch is selected
      if (selectedBranch) {
        filter.employee_Branch_Id = selectedBranch.value;
      }

      // Add department filter if selected
      if (selectedDepartment) {
        filter.employee_Department_ID = selectedDepartment.value;
      }
      
      const response = await getEmployees(filter);
      const employeeData = Array.isArray(response) ? response : (response.data || []);
      
      // Get employees already in payroll details
      const payrollDetailsResponse = await axios.post(`${BASE_URL}/api/PayrollDetail/get`, {
        Payroll_Id: payrollInfo.payroll_Id,
        Business_Id: selectedBusiness.value,
        ...(selectedBranch && { Branch_Id: selectedBranch.value }),
        ...(selectedDepartment && { Department_Id: selectedDepartment.value })
      }, {
        headers: getAuthHeader()
      });
      const existingPayrollEmployees = new Set(
        (Array.isArray(payrollDetailsResponse.data) ? payrollDetailsResponse.data : (payrollDetailsResponse.data?.data || []))
          .map(p => p.employee_Id)
      );

      // Get branches for mapping
      const branchResponse = await getBranches({ Business_Id: selectedBusiness.value });
      const branchData = Array.isArray(branchResponse) ? branchResponse : (branchResponse.data || []);
      const branchMap = new Map(branchData.map(b => [b.branch_Id, b.branch_NameEn || b.branch_Name]));

      // Get employee leaves to find unpaid leaves
      const leavesResponse = await axios.post(`${BASE_URL}/api/EmployeeLeave/get`, {}, {
        headers: getAuthHeader(),
      });
      const leavesData = leavesResponse.data?.data || [];
      
      // Calculate date range for unpaid leaves and salary advances
      const payrollDate = new Date(payrollInfo.payroll_Date);
      const startDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth() - 1, payrollDate.getDate());
      const endDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth(), payrollDate.getDate() - 1);
      
      // For incentive calculations, use FULL PREVIOUS MONTH
      const incentiveStartDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth() - 1, 1); // First day of previous month
      const incentiveEndDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth(), 0); // Last day of previous month

      // For incentive calculations, use simple YYYY-MM-DD format to avoid timezone issues
      const incentiveStartDateStr = `${payrollDate.getFullYear()}-${String(payrollDate.getMonth()).padStart(2, '0')}-01`; // Previous month, day 1
      const incentiveEndDateStr = `${payrollDate.getFullYear()}-${String(payrollDate.getMonth()).padStart(2, '0')}-${new Date(payrollDate.getFullYear(), payrollDate.getMonth(), 0).getDate().toString().padStart(2, '0')}`; // Previous month, last day
      
      // Create a map of employee unpaid leaves within the date range
      const unpaidLeaveMap = new Map();
      leavesData.forEach(leave => {
        // Only count unpaid leaves (type 9) that are not cancelled
        if (leave.leave_LeaveType_Id === 9 && 
            !leave.is_Cancelled) {
          
          const leaveStartDate = new Date(leave.leave_FromDate);
          const leaveEndDate = new Date(leave.leave_ToDate);
          
          // Check if leave period overlaps with our target period
          if (leaveStartDate <= endDate && leaveEndDate >= startDate) {
            const employeeId = leave.leave_Employee_Id;
            const currentDays = unpaidLeaveMap.get(employeeId) || 0;
            
            // Calculate actual unpaid days within payroll period
            let effectiveEndDate;
            
            // If rejoin date exists, use it; otherwise employee is still on leave till payroll cutoff
            if (leave.leave_RejoinDate) {
              effectiveEndDate = new Date(leave.leave_RejoinDate);
            } else {
              effectiveEndDate = endDate; // Use payroll cutoff date since employee is still on unpaid leave
            }
            
            // The actual end date should not exceed payroll cutoff date
            const actualEndDate = effectiveEndDate > endDate ? endDate : effectiveEndDate;
            const actualStartDate = leaveStartDate < startDate ? startDate : leaveStartDate;
            
            // Calculate actual days within payroll period
            if (actualStartDate <= actualEndDate) {
              const actualDays = Math.ceil((actualEndDate - actualStartDate) / (1000 * 60 * 60 * 24)) + 1;
              const daysToAdd = leave.is_HalfDay ? actualDays * 0.5 : actualDays;
              unpaidLeaveMap.set(employeeId, currentDays + daysToAdd);
            }
          }
        }
      });

      // Get incentive data for calculations
      const [daybookResponse, salesResponse, percentageResponse] = await Promise.all([
        axios.post(`${BASE_URL}/api/Daybook/get`, {}, { headers: getAuthHeader() }),
        axios.post(`${BASE_URL}/api/Sales/get`, {}, { headers: getAuthHeader() }),
        axios.post(`${BASE_URL}/api/PercentageCalculation/get`, { Active: true }, { headers: getAuthHeader() })
      ]);

      const daybookData = daybookResponse.data || [];
      const salesData = salesResponse.data || [];
      const percentageData = percentageResponse.data || [];

      console.log('=== INCENTIVE CALCULATION DEBUG ===');
      console.log('Payroll Date Range (Advances/Absent):', { startDate: startDate.toISOString(), endDate: endDate.toISOString() });
      console.log('Incentive Date Range (Clean Monthly):', { incentiveStartDateStr, incentiveEndDateStr });
      console.log('Raw Daybook Data:', daybookData.length, 'records');
      console.log('Raw Sales Data:', salesData.length, 'records');
      console.log('Raw Percentage Data:', percentageData);

      // Separate percentage data by type
      const performanceSlabs = percentageData.filter(p => p.type === 1).sort((a, b) => a.number - b.number);
      const salesRates = percentageData.filter(p => p.type === 2);

      console.log('Performance Slabs (Type 1):', performanceSlabs);
      console.log('Sales Rates (Type 2):', salesRates);

      // Create maps for performance and sales incentives within date range
      const performanceIncentiveMap = new Map();
      const salesIncentiveMap = new Map();

      // Calculate performance incentives from daybook data
      daybookData.forEach(daybook => {
        // Check if transaction_DateTime exists before processing
        if (daybook.transaction_DateTime) {
          // Convert to YYYY-MM-DD format for simple comparison
          const transactionDateStr = daybook.transaction_DateTime.substring(0, 10); // Get YYYY-MM-DD part
          const isInRange = transactionDateStr >= incentiveStartDateStr && transactionDateStr <= incentiveEndDateStr;
          
          if (isInRange) {
            const employeeId = daybook.employee_Id;
            const currentCost = performanceIncentiveMap.get(employeeId) || 0;
            performanceIncentiveMap.set(employeeId, currentCost + (daybook.cost || 0));
            console.log(`Performance - Employee ${employeeId}: +${daybook.cost} = ${currentCost + (daybook.cost || 0)} (Date: ${transactionDateStr})`);
          }
        }
      });

      // Calculate sales incentives from sales data
      salesData.forEach(sale => {
        // Check if transaction_Date exists before processing
        if (sale.transaction_Date) {
          // Convert to YYYY-MM-DD format for simple comparison
          const transactionDateStr = sale.transaction_Date.substring(0, 10); // Get YYYY-MM-DD part
          const isInRange = transactionDateStr >= incentiveStartDateStr && transactionDateStr <= incentiveEndDateStr;
          
          if (isInRange) {
            const employeeId = sale.employee_Id;
            const currentSales = salesIncentiveMap.get(employeeId) || 0;
            salesIncentiveMap.set(employeeId, currentSales + (sale.amount || 0));
            console.log(`Sales - Employee ${employeeId}: +${sale.amount} = ${currentSales + (sale.amount || 0)} (Date: ${transactionDateStr})`);
          }
        }
      });

      console.log('Final Performance Totals:', Object.fromEntries(performanceIncentiveMap));
      console.log('Final Sales Totals:', Object.fromEntries(salesIncentiveMap));

      // Filter and map employee data
      const availableEmployees = employeeData
        .filter(emp => !existingPayrollEmployees.has(emp.employee_Id))
        .map((emp, index) => {
          const branchName = branchMap.get(emp.employee_Branch_Id);
          const designation = designations.find(d => d.designation_Id === emp.employee_Designation_ID);
          const unpaidLeaveDays = unpaidLeaveMap.get(emp.employee_Id) || 0;
          
          // Calculate incentives for this employee
          const grossSalary = emp.employee_GS || 0;
          const performanceTotal = performanceIncentiveMap.get(emp.employee_Id) || 0;
          const salesTotal = salesIncentiveMap.get(emp.employee_Id) || 0;
          
          console.log(`\n=== EMPLOYEE ${emp.employee_Id} (${emp.employee_Code}) ===`);
          console.log(`Gross Salary: ${grossSalary}`);
          console.log(`Performance Total from Map: ${performanceTotal}`);
          console.log(`Sales Total from Map: ${salesTotal}`);
          
          const performanceIncentive = calculateSlabBasedIncentive(performanceTotal, grossSalary, performanceSlabs);
          const salesIncentive = calculateFlatRateIncentive(salesTotal, salesRates);
          
          console.log(`Final Performance Incentive: ${performanceIncentive}`);
          console.log(`Final Sales Incentive: ${salesIncentive}`);
          
          return {
            ...emp,
            serialNumber: index + 1,
            employee_Name: emp.employee_NameEn || emp.employee_Name,
            business_Name: selectedBusiness.label,
            branch_Name: branchName || 'N/A',
            designation_Name: designation ? (designation.designation_NameEn || designation.designation_Name) : 'N/A',
            basic_Salary: emp.employee_BS || 0,
            hra: emp.employee_HRA || 0,
            transport_Allowance: emp.employee_TA || 0,
            other_Allowance: emp.employee_OA || 0,
            food_Allowance: emp.employee_FoodAllowance || 0,
            communication_Allowance: emp.employee_CommunicationAllowance || 0,
            utility_Allowance: emp.employee_UtilityAllowance || 0,
            wps_Salary: emp.wpsSalary || 0,
            gross_Salary: emp.employee_GS || 0,
            absent_Days: unpaidLeaveDays,
            // Add incentive information
            performance_Incentive: performanceIncentive,
            sales_Incentive: salesIncentive,
            performanceTotal: performanceTotal,
            salesTotal: salesTotal
          };
        });

      setEmployees(availableEmployees);
      setSelectedEmployees([]);
      setSelectAll(false);
    } catch (error) {
      console.error("Error loading employees:", error);
      Swal.fire("Error", error.message || "Failed to load employees", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessChange = (selected) => {
    setSelectedBusiness(selected);
    setSelectedBranch(null);
    setSelectedDepartment(null);
    setBranches([]);
    setDepartments([]);
    setEmployees([]);
    setSelectedEmployees([]);
    setHasAppliedFilter(false);
    if (selected) {
      loadBranches(selected.value);
    }
  };

  const handleBranchChange = (selected) => {
    setSelectedBranch(selected);
    setSelectedDepartment(null);
    setDepartments([]);
    if (selected) {
      loadDepartments(selected.value);
    }
  };

  const handleDepartmentChange = (selected) => {
    setSelectedDepartment(selected);
  };

  const handleApplyFilter = () => {
    if (!selectedBusiness) {
      Swal.fire("Warning", "Please select a business first", "warning");
      return;
    }
    setHasAppliedFilter(true);
    loadEmployees();
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      setSelectedEmployees(employees.map(emp => emp.employee_Id));
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleEmployeeSelect = (employeeId) => {
    setSelectedEmployees(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  const handleAbsentDaysChange = (employeeId, value) => {
    setEmployees(prev =>
      prev.map(emp =>
        emp.employee_Id === employeeId
          ? { ...emp, absent_Days: value }
          : emp
      )
    );
  };

  const handleAddToPayroll = async () => {
    if (!payrollInfo) {
      Swal.fire("Error", "No active payroll found", "error");
      return;
    }

    if (selectedEmployees.length === 0) {
      Swal.fire("Warning", "Please select at least one employee", "warning");
      return;
    }

    setLoading(true);
    try {
      for (const employeeId of selectedEmployees) {
        // Find employee details including absent days
        const employee = employees.find(emp => emp.employee_Id === employeeId);
        
        // Save/update absent days
        const absentDay = {
          Absent_Employee_Id: employeeId,
          Absent_Days: employee.absent_Days || 0,
          Absent_ModifiedDate: new Date().toISOString(),
          Absent_ModifiedBy: Number(localStorage.getItem("userId")) || 1
        };

        await saveAbsentDay(absentDay);
        
        // Use employee_GS as gross salary without modification
        const grossSalary = employee.employee_GS || 0;
        const dailySalary = grossSalary / 30;
        const absentDeduction = dailySalary * (employee.absent_Days || 0);
        
        // Calculate salary advance balance with date range validation
        let totalAdvanceAmount = 0;
        let totalAdvanceDeduction = 0;
        
        try {
          // Calculate date range for salary advances (same as payroll processing)
          const payrollDate = new Date(payrollInfo.payroll_Date);
          const startDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth() - 1, payrollDate.getDate());
          const endDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth(), payrollDate.getDate() - 1);

          // Get salary advances within the date range
          const advanceResponse = await axios.post(`${BASE_URL}/api/SalaryAdvance/get`, {
            Employee_Id: employeeId
          }, {
            headers: getAuthHeader()
          });
          
          const advances = advanceResponse.data || [];
          
          // Filter advances that are approved within our date range
          const validAdvances = advances.filter(adv => {
            const approvalDate = new Date(adv.approved_Date);
            return adv.approved_Amount > 0 && 
                   approvalDate >= startDate && 
                   approvalDate <= endDate;
          });

          // Calculate total advance amount from valid advances
          totalAdvanceAmount = validAdvances.reduce((sum, adv) => sum + (adv.approved_Amount || 0), 0);

          if (totalAdvanceAmount > 0) {
            // Get transactions to calculate remaining balance
            const transResponse = await axios.post(`${BASE_URL}/api/SalaryAdvanceTransaction/get`, {
              employee_Id: employeeId
            }, {
              headers: getAuthHeader()
            });
            
            const transactions = transResponse.data || [];
            
            // Calculate remaining balance (start with valid advances, subtract recoveries)
            let remainingBalance = totalAdvanceAmount;
            transactions.forEach(trans => {
              if (trans.transactionType === 'PayrollDetails') {
                remainingBalance -= trans.recovered_Amount || 0;
              }
            });

            // Set final values
            totalAdvanceAmount = Math.max(0, remainingBalance);
            totalAdvanceDeduction = Math.min(
              validAdvances.reduce((sum, adv) => sum + (adv.monthly_Deduction || 0), 0),
              totalAdvanceAmount
            );
          }
        } catch (error) {
          console.error(`Error calculating advance balance for employee ${employeeId}:`, error);
          totalAdvanceAmount = 0;
          totalAdvanceDeduction = 0;
        }

        // Get incentive values
        const performanceIncentive = employee.performance_Incentive || 0;
        const salesIncentive = employee.sales_Incentive || 0;

        // Calculate total earnings (WPS is NOT part of earnings - it's just a payment method)
        const earnings = [
          employee.employee_BS || 0,
          employee.employee_HRA || 0,
          employee.employee_TA || 0,
          employee.employee_OA || 0,
          employee.employee_FoodAllowance || 0,
          employee.employee_CommunicationAllowance || 0,
          employee.employee_UtilityAllowance || 0
        ].reduce((sum, amount) => sum + amount, 0);

        // Calculate net salary using earnings plus incentives minus deductions
        const netSalary = earnings + performanceIncentive + salesIncentive - absentDeduction - totalAdvanceDeduction;
        
        // Create payroll detail
        const payrollDetail = {
          Payroll_Id: payrollInfo.payroll_Id,
          Employee_Id: employeeId,
          Business_Id: selectedBusiness.value,
          Branch_Id: employee.employee_Branch_Id || selectedBranch?.value || 1,
          Department_Id: employee.employee_Department_ID || selectedDepartment?.value || 1,
          Absent_Days: parseInt(employee.absent_Days || 0),
          Basic_Salary: employee.employee_BS || 0,
          HRA: employee.employee_HRA || 0,
          Transport_Allowance: employee.employee_TA || 0,
          Other_Allowance: employee.employee_OA || 0,
          Food_Allowance: employee.employee_FoodAllowance || 0,
          Communication_Allowance: employee.employee_CommunicationAllowance || 0,
          Utility_Allowance: employee.employee_UtilityAllowance || 0,
          WPS_Salary: employee.wpsSalary || 0,
          Gross_Salary: earnings, // Use calculated earnings as gross salary (without WPS)
          Absent_Deduction: absentDeduction,
          SalaryAdvance_Amount: totalAdvanceAmount,
          SalaryAdvance_Deduction: totalAdvanceDeduction,
          Performance_Incentive: performanceIncentive,
          Sales_Incentive: salesIncentive,
          Other_Additions: 0,
          Other_Deductions: 0,
          Net_Salary: netSalary,
          IsApproved: false,
          IsPayslip_Generated: false,
          Modified_By: Number(localStorage.getItem("userId")) || 1,
          Modified_Date: new Date().toISOString(),
          Approved_By: null,
          Approved_Date: null
        };

        const payrollResponse = await savePayrollDetail(payrollDetail);
        if (!payrollResponse.msg || payrollResponse.msg.toLowerCase().includes('error')) {
          throw new Error(`Failed to save payroll details for employee ${employeeId}: ${payrollResponse.msg || 'Unknown error'}`);
        }
      }

      Swal.fire("Success", "Successfully added to payroll", "success");
      loadEmployees(); // Refresh the list
    } catch (error) {
      console.error("Error adding to payroll:", error);
      Swal.fire("Error", error.message || "Failed to add to payroll", "error");
    } finally {
      setLoading(false);
    }
  };

  const getProcessingMonthText = (payrollDate) => {
    if (!payrollDate) return '';
    const date = new Date(payrollDate);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const columns = [
    {
      header: ({ table }) => (
        <div className="form-check">
          <Input
            type="checkbox"
            className="form-check-input"
            checked={selectAll}
            onChange={handleSelectAll}
          />
        </div>
      ),
      accessorKey: "select",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="form-check">
          <Input
            type="checkbox"
            className="form-check-input"
            checked={selectedEmployees.includes(row.original.employee_Id)}
            onChange={() => handleEmployeeSelect(row.original.employee_Id)}
          />
        </div>
      ),
      size: 50
    },
    {
      header: "Sr No",
      accessorKey: "serialNumber",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => row.original.serialNumber,
      size: 70
    },
    {
      header: "Employee Code",
      accessorKey: "employee_Code",
      enableColumnFilter: false,
    },
    {
      header: "Name",
      accessorKey: "employee_Name",
      enableColumnFilter: false,
    },
    {
      header: "Designation",
      accessorKey: "designation_Name",
      enableColumnFilter: false,
    },
    {
      header: "Gross Salary",
      accessorKey: "gross_Salary",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div style={{ textAlign: "right" }}>
          {(row.original.gross_Salary || 0).toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          })}
        </div>
      ),
      size: 120
    },
    {
      header: "Performance Incentive",
      accessorKey: "performance_Incentive",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div style={{ textAlign: "right" }}>
          {(row.original.performance_Incentive || 0).toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          })}
        </div>
      ),
      size: 140
    },
    {
      header: "Sales Incentive",
      accessorKey: "sales_Incentive",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div style={{ textAlign: "right" }}>
          {(row.original.sales_Incentive || 0).toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          })}
        </div>
      ),
      size: 130
    },
    {
      header: "Absent Days",
      accessorKey: "absent_Days",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div style={{ minWidth: "120px" }}>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={row.original.absent_Days ?? ''}
            onChange={(e) => handleAbsentDaysChange(row.original.employee_Id, e.target.value)}
            className="form-control"
            style={{ width: "100%" }}
            disabled={!hasEditOrHigher}
          />
        </div>
      ),
      size: 120
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="card-title mb-0">Mark Absent Days</h4>
              {payrollInfo && (
                <h5 className="text-muted mb-0">
                  Processing Month: {getProcessingMonthText(payrollInfo.payroll_Date)}
                </h5>
              )}
            </div>
            <Form>
              <Row>
                <Col md={3}>
                  <FormGroup>
                                                <Label>Company</Label>
                    <Select
                      value={selectedBusiness}
                      onChange={handleBusinessChange}
                      options={businesses}
                      isClearable
                      placeholder="Select Company"
                      maxMenuHeight={80}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Branch (Optional)</Label>
                    <Select
                      value={selectedBranch}
                      onChange={handleBranchChange}
                      options={branches}
                      isClearable
                      isDisabled={!selectedBusiness}
                      placeholder="Select Branch"
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Department (Optional)</Label>
                    <Select
                      value={selectedDepartment}
                      onChange={handleDepartmentChange}
                      options={departments}
                      isClearable
                      isDisabled={!selectedBranch}
                      placeholder="Select Department"
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>&nbsp;</Label>
                    <div>
                      <Button
                        color="primary"
                        onClick={handleApplyFilter}
                      >
                        Apply Filter
                      </Button>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </Form>

            {loading ? (
              <div className="text-center mt-4">
                <ClipLoader color="#123abc" loading={loading} size={50} />
              </div>
            ) : (
              <>
                {hasAppliedFilter && employees.length === 0 ? (
                  <div className="text-center mt-4">
                    <p>No employees available for marking absent days.</p>
                  </div>
                ) : (
                  <>
                    {employees.length > 0 && (
                      <>
                        <TableContainer
                          columns={columns}
                          data={employees}
                          className="custom-header-css mt-4"
                        />
                        {hasEditOrHigher && (
                          <div className="d-flex justify-content-end mt-3">
                            <Button
                              color="primary"
                              onClick={handleAddToPayroll}
                              disabled={selectedEmployees.length === 0}
                            >
                              Add to Payroll
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default AbsentDaysPage; 